"use client"

import * as React from "react"
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    // useCarousel,
} from "@/shared/components/ui/carousel"
import Image from "next/image"
import Autoplay from "embla-carousel-autoplay"
import { mockTenders } from "@/data/tenders"
import { ArrowRight, ChevronRight, Clock4, Eye, Hourglass, MapPin, MessageCircle } from "lucide-react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useLocale, useTranslations } from "next-intl";
import { SectionHeader } from "@/shared/components/home";

export function ActiveTenders() {
    const plugin = React.useRef(
        Autoplay({ delay: 1500, stopOnInteraction: false })
    )
    const pathname = usePathname();
    const isOnTenderPage = pathname.startsWith('/tender/');
    const isOnCartPage = pathname.startsWith('/cart');
    const router = useRouter()
    const t = useTranslations("activeTenders");
    const locale = useLocale();
    return (
        <section id="active-tenders" className="home-dotgrid w-full scroll-mt-24 bg-home-cream py-10 md:py-20">
            <div className="container relative">
                <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
                    <SectionHeader
                        animated
                        align="start"
                        className="min-w-0"
                        eyebrow={t("subtitle")}
                        title={t("title")}
                        highlight={t("highlight")}
                    />
                    <Link
                        href={`/${locale}/tender`}
                        className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-home-pill border-[1.5px] border-home-navy bg-transparent px-6 py-[14px] text-[17px] font-bold text-home-navy hover:bg-home-navy hover:text-home-cream focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-home-navy"
                    >
                        {t("button")}
                        <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.6} aria-hidden="true" />
                    </Link>
                </div>

                <div className="relative flex flex-col items-center gap-8">

                    <Carousel
                        opts={{
                            align: "start",
                            loop: true,
                        }}
                        plugins={[plugin.current]}
                        className="w-full"
                    >
                        <CarouselContent className="-ml-4">
                            {mockTenders
                                .filter(tender => tender.status === 'Davam edir')
                                .map((tender, index) => (
                                    <CarouselItem key={index} className="pl-4 basis-1/1 md:basis-1/2 lg:basis-1/2 md:h-[340px]">

                                        <div
                                            key={tender.id}
                                            className="p-6 max-w-[400px] sm:max-w-max h-full rounded-lg bg-brand-cream border border-brand-dot-track hover:bg-[linear-gradient(270deg,_var(--brand-sand)_0%,_var(--brand-cream)_100%)] transition-colors duration-200 flex flex-col gap-4"
                                        >
                                            {/* Company Info (Left - Stacked) */}
                                            <div className=" gap-4 flex-col md:flex-row">
                                                <div className="flex flex-col items-start mb-4 sm:items-start text-center sm:text-left w-full sm:w-auto flex-shrink-0 sm:max-w-[150px] min-w-[240px]">
                                                    <Image
                                                        src={tender.companyLogo}
                                                        alt={tender.companyName}
                                                        width={64}
                                                        height={64}
                                                        className="rounded-full mb-2 object-cover"
                                                        unoptimized
                                                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => { e.currentTarget.src = '/GrandmartLogo.svg'; }}
                                                    />
                                                    <span className="font-medium text-brand-navy break-words">{tender.companyName}</span>
                                                </div>

                                                {/* Main Content (Right) */}
                                                <div className="flex-1 flex flex-col z-10">
                                                    <div className="flex justify-between items-start mb-4 flex-col-reverse gap-2">
                                                        <h3 className="text-[16px] font-semibold text-brand-navy cursor-pointer max-w-[320px] sm:max-w-[400px] md:max-w-max" onClick={() => { router.push(`/tender/${tender?.slug}`) }}>{tender.title}</h3>
                                                        {/* <Badge
                                                            variant={tender.status === "Tamamlanmış" ? "default" : "secondary"}
                                                            className={`${tender.status === "Tamamlanmış" ? 'bg-brand-panel-blue text-brand-muted-slate' : 'bg-brand-accent-soft text-brand-accent-text'} whitespace-nowrap flex-shrink-0`}
                                                        >
                                                            {tender.status}
                                                        </Badge> */}
                                                    </div>

                                                    <p className="text-sm text-brand-muted-light mb-4 line-clamp-2 max-w-[320px] sm:max-w-[400px] md:max-w-max">{tender.description}</p>
                                                    {(!isOnTenderPage && !isOnCartPage) && (
                                                        <Link href={`/login`} className="text-sm text-brand-navy hover:text-brand-accent-text hover:underline flex items-center gap-1">
                                                            Ətraflı <ChevronRight className="w-4 h-4" />
                                                        </Link>
                                                    )}

                                                </div>
                                            </div>
                                            <div className="flex flex-wrap justify-start md:justify-between w-full gap-x-4 gap-y-2 text-xs text-brand-muted-light mt-auto pt-4 border-t border-brand-dot-track">
                                                <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-brand-accent-text" /> {tender.location}</span>
                                                <span className="flex items-center gap-1"><Clock4 className="w-3 h-3 text-brand-accent-text" /> {tender.postedAgo}</span>
                                                <span className="flex items-center gap-1"><Hourglass className="w-3 h-3 text-brand-accent-text" /> {tender.deadline}</span>
                                                <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-brand-accent-text" /> {tender.bids}</span>
                                                <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-brand-accent-text" /> {tender.views}</span>
                                            </div>
                                        </div>
                                    </CarouselItem>
                                ))}
                        </CarouselContent>
                    </Carousel>
                </div>
            </div>
        </section>
    )
}
